import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import { GameCard } from './GameCard.js';
import { GamesGridSkeleton } from './GamesGridSkeleton.js';
import type { HotGameCard } from '@/features/games/hot.js';
import { useGameLaunch } from '@/features/games/useGameLaunch.js';
import { useAuthStore } from '@/stores/auth.js';

/**
 * The lobby's popular slots, in the order `useHotGames` ranked them; cards
 * slide to their new place when a refetch re-ranks them.
 */
export function HotGamesGrid({ games }: { games: HotGameCard[] }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const reduceMotion = useReducedMotion();
  const { launch, modal } = useGameLaunch({ lang: i18n.language ?? 'th' });
  const [launchingId, setLaunchingId] = useState<string | null>(null);

  async function onPlay(game: HotGameCard) {
    if (!isAuthed) {
      void navigate({ to: '/login' });
      return;
    }
    // Popup/redirect providers must launch inside the click's user gesture.
    if (game.launchMode === 'popup' || game.launchMode === 'redirect') {
      setLaunchingId(game.id);
      try {
        await launch(game);
      } finally {
        setLaunchingId(null);
      }
      return;
    }
    void navigate({ to: '/casino/$code', params: { code: game.code } });
  }

  return (
    <>
      <ul className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {games.map((g) => (
          <motion.li key={g.id} layout={!reduceMotion} transition={{ duration: 0.35 }}>
            <GameCard game={g} launching={launchingId === g.id} onPlay={() => void onPlay(g)} />
          </motion.li>
        ))}
      </ul>
      {modal}
    </>
  );
}

export function HotGamesSkeleton() {
  return <GamesGridSkeleton count={6} />;
}
